import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  Multicountry,
  MultiCountryTotalCompletesHeader,
  SelectedCountryInterface,
} from '../shared/interfaces/create-surveys.interface';
import { BuyerSurveyResponse } from '../shared/interfaces/survey.interface';
import { QuotaPayload } from '../shared/interfaces/quota.interface';
import {
  Qualification,
  QualificationPayload,
} from '../shared/interfaces/qualification.interface';
import { MultiCountryDraftService } from './services/multi-country-draft/multi-country-draft.service';
import { ProjectManagers } from '../shared/interfaces/user.interface';
import { QUALIFICATIONS_CATEGORIES } from '../shared/constants/qualification-categories';

@Injectable({
  providedIn: 'root',
})
export class CreateSurveyService {
  constructor(private _multiCountryDraftService: MultiCountryDraftService) {}

  private _multiCountryData = new BehaviorSubject<MultiCountryData>({
    selectedCountries: [],
    isDraft: false,
    expectedIr: 0,
    clickBalancing: 0,
    completesRequired: 0,
    expectedLoi: 0,
    fieldTime: 0,
    source: 'platform',
    surveyCategoryCode: 0,
    surveyTitle: '',
    surveyId: 0,
    qualifications: [],
    payloadQualifications: [],
    quotas: [],
  });
  private _isDraftSaved = new BehaviorSubject<boolean>(false);
  private _multiCountrySurveys = new BehaviorSubject<Multicountry[]>([]);
  private _multiCountryQuota = new Subject<MultiCountryQuotaData>();
  private _liveUrlForAllSurveys = new BehaviorSubject<LiveUrlAndTestUrl>({
    live_url: '',
    test_url: '',
  });
  private _surveyPoForAllSurveys = new BehaviorSubject<string>('');
  private _surveyProjectManagerForAllSurveys =
    new BehaviorSubject<ProjectManagers>({} as ProjectManagers);
  private _multiCountryTotalCompletes$ =
    new BehaviorSubject<MultiCountryTotalCompletesHeader>({
      showTotalCompletes: false,
      totalCompletes: 0,
      totalCompleteLabel: '',
    });

  private _isPurePriceApplied = new BehaviorSubject<boolean>(false);
  private _isManualProcessingPermission = new BehaviorSubject<boolean>(true);

  setMultiCountryData(data: MultiCountryData) {
    this._multiCountryData.next(data);
  }

  getMultiCountryData(): Observable<MultiCountryData> {
    return this._multiCountryData.asObservable();
  }

  getMultiCountryQuotasData(): Observable<MultiCountryQuotaData> {
    return this._multiCountryQuota.asObservable();
  }
  setMultiCountryQuotasData(data: MultiCountryQuotaData) {
    this._multiCountryQuota.next(data);
  }

  setIsDraftSavedValue(isDraftSaved: boolean) {
    this._isDraftSaved.next(isDraftSaved);
  }

  getIsDraftSavedValue(): Observable<boolean> {
    return this._isDraftSaved.asObservable();
  }

  setMultiCountrySurveys(data: Multicountry[]) {
    this._multiCountrySurveys.next(data);
  }

  getMultiCountrySurveys(): Observable<Multicountry[]> {
    return this._multiCountrySurveys.asObservable();
  }

  setLiveUrlForAllSurveys(data: LiveUrlAndTestUrl) {
    this._liveUrlForAllSurveys.next({
      live_url: data.live_url,
      test_url: data.test_url,
    });
  }

  getLiveUrlForAllSurveys(): Observable<LiveUrlAndTestUrl> {
    return this._liveUrlForAllSurveys.asObservable();
  }

  setBillingSurveyPoForAllSurveys(survey_po: string) {
    this._surveyPoForAllSurveys.next(survey_po);
  }

  getBillingSurveyPoForAllSurveys(): Observable<string> {
    return this._surveyPoForAllSurveys.asObservable();
  }

  getProjectManagerForAllSurveys(): Observable<ProjectManagers> {
    return this._surveyProjectManagerForAllSurveys.asObservable();
  }

  setProjectManagerForAllSurveys(bpm: ProjectManagers): void {
    this._surveyProjectManagerForAllSurveys.next(bpm);
  }

  mapMultiCouuntrySurveyData(surveys: BuyerSurveyResponse[]): Multicountry[] {
    return surveys.map((survey, index) => {
      return {
        name: survey.locale.country_name,
        id: index,
        short_Code: survey.locale.country_code,
        pl_code: survey.locale.country_code,
        language: {
          id: index,
          name: survey.locale.language_name,
          short_code: survey.locale.language_code,
          transalte_code: survey.locale.language_translate,
        },
        checked: true,
        countryLanguageCode: `${survey.locale.country_code}-${survey.locale.language_code}`,
        expectedIr: survey.expected_ir,
        expectedLoi: survey.expected_loi,
        surveyId: survey.ps_survey_id,
        psSurveystatus: survey.ps_survey_status,
        supplier_ids: [],
        surveyTitle: survey.survey_title,
        surveyCategoryCode: survey.survey_category_code,
        completesRequired: survey.completes_required,
        fieldTime: survey.field_time,
        payloadQualifications: survey.qualifications,
        quotas: survey.quotas,
        priceOverride: Number(survey.offer_price),
        cpi: survey.acpi ?? (survey.cpi.current || 0),
        offerPrice: Number(survey.offer_price),
        buyerMargin:
          survey.margin_override && survey.margin_override.margin
            ? survey.margin_override.margin
            : 0,
        manualProcessingLoading: false,
      };
    });
  }

  resetMultiCountryData() {
    this._multiCountryData.next({} as MultiCountryData);
    this._isDraftSaved.next(false);
    this._multiCountrySurveys.next([] as Multicountry[]);
    this._liveUrlForAllSurveys.next({
      live_url: '',
      test_url: '',
    });
    this._surveyPoForAllSurveys.next('');
    this._multiCountryTotalCompletes$.next({
      showTotalCompletes: false,
      totalCompletes: 0,
      totalCompleteLabel: '',
    });
    this._multiCountryDraftService.setMultiCountryDraftData({
      countryData: [],
    });
  }

  setMultiCountryTotalCompletes(data: MultiCountryTotalCompletesHeader) {
    this._multiCountryTotalCompletes$.next(data);
  }

  getMultiCountryTotalCompletes() {
    return this._multiCountryTotalCompletes$.asObservable();
  }

  setIsPurePriceApplied(value: boolean) {
    this._isPurePriceApplied.next(value);
  }

  getIsPurePrieApplied(): Observable<boolean> {
    return this._isPurePriceApplied.asObservable();
  }

  setIsManualProcessingPermission(value: boolean) {
    this._isManualProcessingPermission.next(value);
  }

  getIsManualProcessingPermission(): Observable<boolean> {
    return this._isManualProcessingPermission.asObservable();
  }

  addQuotaCategory(quotas: QuotaPayload[]) {
    return quotas.map((quota) => {
      const criteria = quota.criteria[0];
      const conditions =
        (criteria.condition_codes?.length || criteria.range_sets?.length) ?? 0;

      if (conditions == 1 && !quota.quota_category) {
        quota.quota_category = QUALIFICATIONS_CATEGORIES.LAYERED;
      } else if (conditions > 1 && !quota.quota_category) {
        quota.quota_category = QUALIFICATIONS_CATEGORIES.GROUPED;
      }
      return quota;
    });
  }
}

export interface MultiCountryData {
  selectedCountries: SelectedCountryInterface[];
  isDraft: boolean;
  expectedIr: number;
  clickBalancing: number;
  completesRequired: number;
  expectedLoi: number;
  fieldTime: number;
  source: string;
  surveyCategoryCode: number;
  surveyTitle: string;
  surveyId: number;
  qualifications: Array<Qualification>;
  payloadQualifications: Array<QualificationPayload>;
  quotas: Array<QuotaPayload>;
  psSurveystatus?: number;
  offerPrice?: number;
  buyerMargin?: number;
}

export interface LiveUrlAndTestUrl {
  live_url: string;
  test_url: string;
}

export interface MultiCountryQuotaData {
  surveyId: number;
  qualifications: Array<Qualification>;
  payloadQualifications: Array<QualificationPayload>;
  quotas: Array<QuotaPayload>;
}
